import React, { useState, useEffect, useCallback } from 'react';
import { useUpdateEmailAndSend } from '../../../../hooks/admin/email/email';
import { toast } from 'react-toastify';

// Interfaces
interface Question {
  id?: number;
  question: string;
  answer: string;
  category: string;
  status: 'Pending' | 'Approved' | 'Rejected';
  source: string;
  lastUpdated: string;
  email?: string;
}

export interface Email {
  id: number;
  sender_email: string;
  message: string;
  ai_generated_reply: string;
  subject: string;
  sent_at: string | null;
  is_approved: boolean;
  created_at: string;
}

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: Question) => void;
  initialData?: Question;
  isLoading?: boolean;
}

// Default empty question
const defaultQuestion: Question = {
  question: '',
  answer: '',
  category: '',
  status: 'Pending',
  source: '',
  lastUpdated: new Date().toISOString().split('T')[0], // Today's date in YYYY-MM-DD
};

// Question Modal Component
const QuestionModal: React.FC<ModalProps> = ({ 
  isOpen, 
  onClose, 
  onSave, 
  initialData,
  isLoading = false 
}) => {
  const [questionData, setQuestionData] = useState<Question>(defaultQuestion);
  const [errors, setErrors] = useState<Partial<Record<keyof Question, string>>>({});
  const [touched, setTouched] = useState<Set<keyof Question>>(new Set());
  
  // Move hooks to the top level - before any conditionals
  const sendMailAPI = useUpdateEmailAndSend();

  const handleSendMail = useCallback((email: any) => {
    toast.dismiss();
    console.log(email)
    if (!email.answer.trim()) {
      toast.error("Cannot send empty reply");
      return;
    }

    toast.loading("Sending mail...");

    sendMailAPI.mutate({
      id: email.id,
      is_updated: false,
      new_reply: email.answer,
      status:"sent"
    }, {
      onSuccess: () => {
        toast.dismiss();
        toast.success("Mail sent successfully");
        // refetch(); // Refresh the data
      },
      onError: (error) => {
        toast.dismiss();
        console.error("Send mail error:", error);
        toast.error("Unable to send mail. Please try again.");
      }
    });
  }, [sendMailAPI]);

  // Reset form when modal opens/closes or initialData changes
  useEffect(() => {
    if (isOpen) {
      setQuestionData(initialData || defaultQuestion);
      setErrors({});
      setTouched(new Set());
    }
  }, [isOpen, initialData]);

  // Handle input changes with validation
  const handleChange = useCallback((
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    const fieldName = name as keyof Question;
    
    setQuestionData(prev => ({ ...prev, [fieldName]: value }));
    
    // Mark field as touched
    setTouched(prev => new Set(prev).add(fieldName));
    
    // Clear error when user starts typing
    if (errors[fieldName]) {
      setErrors(prev => ({ ...prev, [fieldName]: undefined }));
    }
  }, [errors]);

  // Validate form
  const validateForm = useCallback((): boolean => {
    const newErrors: Partial<Record<keyof Question, string>> = {};

    // if (!questionData.question.trim()) {
    //   newErrors.question = 'Question is required';
    // }

    // if (!questionData.answer.trim()) {
    //   newErrors.answer = 'Answer is required';
    // }

    // if (!questionData.category.trim()) {
    //   newErrors.category = 'Category is required';
    // }

    // if (!questionData.source.trim()) {
    //   newErrors.source = 'Source is required';
    // }

    // if (!questionData.lastUpdated) {
    //   newErrors.lastUpdated = 'Last updated date is required';
    // }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [questionData]);

  // Handle form submission
  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    
    // Mark all fields as touched
    const allFields: (keyof Question)[] = ['question', 'answer', 'category', 'status', 'source', 'lastUpdated'];
    setTouched(new Set(allFields));
    
    if (validateForm()) {
      onSave(questionData);
    }
  }, [questionData, onSave, validateForm]);

  // Handle modal backdrop click
  const handleBackdropClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  // Handle escape key press
  useEffect(() => {
    const handleEscapeKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const isEditing = !!initialData;

  // Helper to check if field has error and was touched
  const hasError = (field: keyof Question): boolean => {
    return !!errors[field] && touched.has(field);
  };

  return (
    <div 
      className="fixed inset-0 bg-gray-500 bg-opacity-75 flex justify-center items-center z-50 p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-800">
              {isEditing ? 'Update Email Response' : 'Add New Email Response'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isLoading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="overflow-y-auto max-h-[calc(90vh-8rem)]">
          <div className="p-6 space-y-6">
            {/* Email Display (if editing) */}
            {isEditing && questionData.email && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sender Email
                </label>
                <div className="p-3 bg-gray-50 rounded border border-gray-200 text-gray-600">
                  {questionData.email}
                </div>
                <p className="text-xs text-gray-500 mt-1">This field cannot be edited</p>
              </div>
            )}

            {/* Question */}
            <div>
              <label htmlFor="question" className="block text-sm font-medium text-gray-700 mb-1">
                Question <span className="text-red-500">*</span>
              </label>
              <textarea
                id="question"
                name="question"
                value={questionData.question}
                onChange={handleChange}
                className={`border rounded-lg w-full px-3 py-2 focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${
                  hasError('question') 
                    ? 'border-red-300 focus:ring-red-500' 
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                rows={3}
                placeholder="Enter the question received via email..."
                disabled={isLoading}
              />
              {hasError('question') && (
                <p className="text-red-500 text-xs mt-1">{errors.question}</p>
              )}
            </div>

            {/* Answer */}
            <div>
              <label htmlFor="answer" className="block text-sm font-medium text-gray-700 mb-1">
                AI Generated Answer <span className="text-red-500">*</span>
              </label>
              <textarea
                id="answer"
                name="answer"
                value={questionData.answer}
                onChange={handleChange}
                className={`border rounded-lg w-full px-3 py-2 focus:outline-none focus:ring-2 focus:border-transparent transition-colors ${
                  hasError('answer') 
                    ? 'border-red-300 focus:ring-red-500' 
                    : 'border-gray-300 focus:ring-blue-500'
                }`}
                rows={5}
                placeholder="Enter or modify the AI-generated answer..."
                disabled={isLoading}
              />
              {hasError('answer') && (
                <p className="text-red-500 text-xs mt-1">{errors.answer}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                This answer will be sent back to the email sender
              </p>
            </div>

          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-transparent transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                onClick={() => handleSendMail(questionData)}
                disabled={sendMailAPI.isLoading}
                className="px-4 py-2 bg-blue-500 border border-transparent rounded-lg text-white font-medium hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {sendMailAPI.isLoading ? (
                  <span className="flex items-center">
                    <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Sending...
                  </span>
                ) : (
                  'Send'
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuestionModal;